import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, ShoppingCart, Eye, MapPin, Clock, Star, Package, AlertTriangle, CheckCircle } from 'lucide-react';
import Button from '../../../components/ui/Button';

const SurpriseBagCard = ({ bag, onPurchase }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDonating, setIsDonating] = useState(false);
  const navigate = useNavigate();

  const handlePurchase = async () => {
    setIsLoading(true);
    try {
      await onPurchase(bag?.id, 'purchase');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDonate = async () => {
    setIsDonating(true);
    try {
      await onPurchase(bag?.id, 'donate');
    } finally {
      setIsDonating(false);
    }
  };

  const handleViewDetails = () => {
    navigate('/surprise-bag-details', { state: { bagId: bag?.id } });
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'high':
        return 'text-red-600';
      case 'medium':
        return 'text-yellow-600';
      default:
        return 'text-green-600';
    }
  };

  const getUrgencyIcon = (urgency) => {
    switch (urgency) {
      case 'high':
        return AlertTriangle;
      case 'medium':
        return Clock;
      default:
        return CheckCircle;
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    })?.format(price);
  };

  // Calculate donation price (typically 20-30% less than discounted price)
  const donationPrice = Math.round(bag?.discountedPrice * 0.75);

  const UrgencyIcon = getUrgencyIcon(bag?.urgency);

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-[1.02]">
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={bag?.image}
          alt={bag?.imageAlt}
          className="w-full h-full object-cover"
        />
        
        {/* Urgency Badge */}
        <div className={`absolute top-3 right-3 flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
          bag?.urgency === 'high' ? 'bg-red-100 text-red-700' :
          bag?.urgency === 'medium'? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
        }`}>
          <UrgencyIcon size={12} />
          <span>{bag?.urgencyLabel}</span>
        </div>

        {/* Discount Badge */}
        <div className="absolute top-3 left-3 bg-green-600 text-white px-2 py-1 rounded-full text-xs font-medium">
          {bag?.discountPercentage}% OFF
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4">
        {/* Business Info */}
        <div className="flex items-center space-x-2 mb-2">
          <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-medium">
            {bag?.businessName?.charAt(0)}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 text-sm">
              {bag?.businessName}
            </h3>
            <div className="flex items-center space-x-1 text-xs text-gray-500">
              <MapPin size={12} />
              <span>{bag?.walkingDistance} min caminando</span>
            </div>
          </div>
        </div>

        {/* Bag Details */}
        <div className="mb-3">
          <h4 className="font-medium text-gray-900 mb-1">
            {bag?.title}
          </h4>
          <p className="text-sm text-gray-600 line-clamp-2">
            {bag?.description}
          </p>
        </div>

        {/* Pricing */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <span className="text-lg font-bold text-gray-900">
              {formatPrice(bag?.discountedPrice)}
            </span>
            <span className="text-sm text-gray-500 line-through">
              {formatPrice(bag?.originalPrice)}
            </span>
          </div>
          <div className="text-xs text-green-600 font-medium">
            Ahorro: {formatPrice(bag?.originalPrice - bag?.discountedPrice)}
          </div>
        </div>

        {/* Pickup Time */}
        <div className="flex items-center space-x-2 mb-4 p-2 bg-gray-50 rounded-md">
          <Clock size={14} className="text-gray-500" />
          <span className="text-sm text-gray-600">
            Recogida: {bag?.pickupTimeStart} - {bag?.pickupTimeEnd}
          </span>
        </div>

        {/* Purchase/Donation Actions */}
        <div className="space-y-3 mb-4">
          {/* Primary Purchase Button */}
          <Button
            className="w-full bg-green-600 hover:bg-green-700 text-white"
            size="sm"
            disabled={isLoading || isDonating}
            onClick={handlePurchase}
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            {isLoading ? 'Comprando...' : `Comprar por ${formatPrice(bag?.discountedPrice)}`}
          </Button>

          {/* Donation Option */}
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              className="flex-1 border-purple-300 text-purple-700 hover:bg-purple-50"
              size="sm"
              disabled={isLoading || isDonating}
              onClick={handleDonate}
            >
              <Heart className="w-4 h-4 mr-2" />
              {isDonating ? 'Donando...' : `Donar por ${formatPrice(donationPrice)}`}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleViewDetails}
              className="text-gray-600 hover:text-gray-900"
            >
              <Eye className="w-4 h-4" />
            </Button>
          </div>

          {/* Donation Info */}
          <div className="bg-purple-50 border border-purple-200 rounded-md p-3">
            <p className="text-xs text-purple-700 font-medium mb-1">
              💜 La donación va a Cáritas Diocesana de Guadalajara
            </p>
            <p className="text-xs text-purple-600">
              <a href="https://www.caritasgdl.org.mx" target="_blank" rel="noopener noreferrer" className="underline hover:text-purple-800">
                www.caritasgdl.org.mx
              </a> - Repartirá la comida entre personas necesitadas y sumará puntos en tu dashboard.
            </p>
          </div>
        </div>

        {/* Additional Info */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-200">
          <div className="flex items-center space-x-1">
            <Star size={14} className="text-yellow-500" />
            <span className="text-sm font-medium text-gray-900">{bag?.rating}</span>
            <span className="text-xs text-gray-500">({bag?.reviewCount})</span>
          </div>
          
          <div className="flex items-center space-x-1">
            <Package size={14} className="text-gray-500" />
            <span className="text-xs text-gray-500">
              {bag?.itemsCount} artículos aprox.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurpriseBagCard;